import { ALL_LEVELS, levelName } from "@edu/shared";
import { SegmentedRadio } from "../../../lib/SegmentedRadio.tsx";
import { ANY_LEVEL_NAME } from "./one-topic-text.ts";

/**
 * Переключатель уровня задания (5.5 docs/homework-phase2-design.md,
 * начертание выбрано владельцем 18.08.2026).
 *
 * ⚠️ Переехал сюда из `features/homework/assign/` 03.09.2026 без единой правки
 * поведения: уровень выбирают уже два экрана, форма выдачи домашки и комната
 * урока. Второй копии переключателя быть не должно, у него нетривиальная
 * клавиатура, и разъехались бы они первой же правкой.
 *
 * ⚠️ **Разметку он больше не рисует сам** (14.09.2026). До этого дня здесь
 * лежала СВОЯ копия классов `.seg` при живом общем компоненте `SegmentedRadio`,
 * со своей навигацией стрелками и своим резервом ширины, хотя в шапке общего
 * прямо написано, что заведён он ради сведения копий. Разъехались они ровно
 * тем, чего не видно глазами: у общего стрелки упирались в выключенный сегмент,
 * у этой копии перепрыгивали его. Перепрыгивание оказалось верным и уехало в
 * общий; здесь не осталось ничего, кроме состава сегментов. Замок на то, что
 * копий больше нет, стоит в `lib/switch-kind.test.ts`.
 *
 * ⚠️ **Сегментов бывает три или четыре, и четвёртый это «любой»** (правка
 * 03.09.2026 по находке ревью). У формы выдачи домашки уровень обязателен:
 * состав работы фиксируется в момент создания, и «какой-нибудь» там означало бы
 * кота в мешке. В комнате урока наоборот: задание собирается по одному, и
 * «любой» это единственный заказ, при котором повторные нажатия ходят по РАЗНЫМ
 * генераторам темы. Сегмент рисуется ровно тогда, когда передан `onAny`, то есть
 * когда есть, что нажать.
 *
 * ⚠️ Резерва ширины под полужирную подпись здесь больше нет и не нужно: у
 * активного сегмента вес подписи не меняется (разбор у `.seg` в `styles.css`),
 * то есть переключатель не дёргается по ширине сам собой. Сегменты при этом
 * стали шириной по подписи, а не равными: подложки, которой нужна была
 * равность, больше нет.
 */
export function LevelPicker(props: {
  /**
   * Выбранный уровень. `null` это «любой» там, где есть `onAny`, и «тема ещё не
   * выбрана» там, где его нет.
   */
  difficulty: number | null;
  /** Уровни, которые есть у темы. Остальные сегменты выключены, но нарисованы. */
  available: readonly number[];
  busy: boolean;
  onDifficulty: (difficulty: number) => void;
  /**
   * Выбрать «любой». Наличие обработчика и есть наличие сегмента: разделять их
   * двумя пропсами значило бы допустить сегмент, который нечем нажать.
   */
  onAny?: (() => void) | undefined;
}) {
  /** Сегменты слева направо. `null` это «любой», и стоит он первым. */
  const segments: (number | null)[] =
    props.onAny === undefined ? [...ALL_LEVELS] : [null, ...ALL_LEVELS];

  /*
    ⚠️ «Любой» выключается только запросом: он выполним у любой темы, какие бы
    уровни у неё ни были. Остальные выключены ещё и тогда, когда такого уровня у
    темы нет, и выключенный сегмент именно РИСУЕТСЯ: спрятанный читается как
    «уровней всего два» и оставляет человека с вопросом, на который экран мог
    ответить.
  */
  const off = (segment: number | null): boolean =>
    props.busy || (segment !== null && !props.available.includes(segment));

  return (
    <SegmentedRadio
      /*
        ⚠️ «Сложность», а не «Уровень»: в той же строке-пике стоит уровень
        материала («Материал: уровень A2»), и это другая шкала. Экран отличает
        их подписью «Материал», а скринридеру группа называется своим именем,
        иначе он прочитал бы два «уровня» подряд как один (раздел 3
        `docs/english-generators-design.md`).
      */
      ariaLabel="Сложность"
      segments={segments.map((segment) => ({
        id: segKey(segment),
        label: segment === null ? ANY_LEVEL_NAME : levelName(segment),
        disabled: off(segment),
      }))}
      /*
        ⚠️ Выбранным считается сегмент, а не `difficulty` сам по себе: уровень
        приходит с сервера и в наш список может не попасть (см.
        `oge/difficulty.ts` в `@edu/shared`). При неизвестном уровне не выбран
        ни один сегмент, потому что отметить чужой хуже, чем не отметить
        никакой: отметка это утверждение о том, что выбрано, и оно было бы
        ложным.
      */
      value={segments.includes(props.difficulty) ? segKey(props.difficulty) : null}
      onPick={(id) => {
        if (id === ANY_KEY) props.onAny?.();
        else props.onDifficulty(Number(id));
      }}
    />
  );
}

/** Имя сегмента «любой». Словом, потому что `null` именем сегмента не бывает. */
const ANY_KEY = "any";

/** Имя сегмента в разметке. Числа собой, «любой» словом. */
function segKey(segment: number | null): string {
  return segment === null ? ANY_KEY : String(segment);
}
